import {
  createRedoCommerceCloudRpcClient,
  RedoCommerceCloudRpcClient,
} from "@redotech/commerce-cloud-sdk/rpc/client";
import { IterableMap } from "@redotech/react-util/component";
import { useLoad } from "@redotech/react-util/load";
import { StyleRoot } from "@redotech/react-util/style";
import { storeTransform } from "@redotech/util/store";
import { sessionStore } from "@redotech/web-util/storage";
import { memo, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { useInterval } from "usehooks-ts";
import { ExchangeSelectBanner } from "../exchange-select.module";
import styleManager from "../style-manager";
import { log, removeItem, useCart } from "../util";
import { CartCard } from "./cart-card";

console.log("Latest 1006");
const REDO_ELEMENT_SELECTOR = ".redo-card";

export const initializeCartCard = ({ widgetId }: { widgetId: string }) => {
  const client = createRedoCommerceCloudRpcClient({
    baseURL: new URL("https://commerce-cloud-server.getredo.com/rpc"),
    headers: { "x-widget-id": widgetId },
    defaultMethod: "GET",
  });

  const rootElement = document.createElement("div");
  rootElement.id = "redo-commerce-cloud-widget";

  const root = createRoot(rootElement);

  root.render(<App client={client} widgetId={widgetId} />);
};

const App = memo(function App({
  client,
  widgetId,
}: {
  client: RedoCommerceCloudRpcClient;
  widgetId: string;
}) {
  const exchangeSelectStore = storeTransform(
    sessionStore("redo.exchange-select"),
    {
      read: (string) => string !== undefined,
      write: (value) => (value ? "" : undefined),
    },
  );

  const startExchange = location.search.includes("redoExchangeSelect");
  if (startExchange) {
    exchangeSelectStore.set(true);
  }

  const [containers, setContainers] = useState<HTMLElement[]>([]);

  const config = useLoad(async (signal) => {
    return client.getStorefrontConfig({ widgetId }, { signal });
  }, []);

  const cart = useCart();

  useEffect(() => {
    const style = document.createElement("style");
    document.head.append(style);
    if (
      config.value?.enabled &&
      config.value.hideProduct?.enabled &&
      config.value.hideProduct.selector
    ) {
      style.innerText = `${config.value.hideProduct.selector} { display: none; } `;
    }
    return () => {
      style.remove();
    };
  }, [config?.value]);

  const bannerContainer = useMemo(() => {
    const element = document.createElement("div");
    element.id = "redo-banner";
    document.body.append(element);
    return element;
  }, []);

  const exchangeSelect = exchangeSelectStore.get() && opener;

  useLoad(
    async (signal) => {
      if (exchangeSelectStore.get()) {
        for (const item of cart?.items ?? []) {
          if (item.brand === "re:do") {
            log("Removing redo because shop on site", { item });
            await removeItem(item, signal);
          }
        }
      }
    },
    [cart],
  );

  useInterval(() => {
    if (exchangeSelect || !config.value?.enabled) {
      return;
    }
    const result: HTMLElement[] = [];
    const togglePlacement = config.value.togglePlacement || "before";
    for (const container of document.querySelectorAll(
      config.value.toggleSelector,
    )) {
      let redoContainer: HTMLElement | null = null;
      switch (togglePlacement) {
        case "before":
        case "after":
          redoContainer =
            container.parentElement?.querySelector(REDO_ELEMENT_SELECTOR) ||
            null;
          break;
        case "prepend":
        case "append":
          redoContainer = container.querySelector(REDO_ELEMENT_SELECTOR);
          break;
      }
      if (!redoContainer) {
        redoContainer = document.createElement("div");
        redoContainer.className = "redo-card";
        switch (togglePlacement) {
          case "before":
            container.before(redoContainer);
            break;
          case "after":
            container.after(redoContainer);
            break;
          case "prepend":
            container.prepend(redoContainer);
            break;
          case "append":
            container.append(redoContainer);
            break;
        }
      }
      result.push(redoContainer);
    }
    setContainers(result);
  }, 100);

  return (
    <>
      <IterableMap items={containers}>
        {(container) => (
          <StyleRoot container={container} styleManager={styleManager}>
            {config.value?.enabled ? <CartCard config={config.value} /> : null}
          </StyleRoot>
        )}
      </IterableMap>
      {exchangeSelect && (
        <StyleRoot container={bannerContainer} styleManager={styleManager}>
          <ExchangeSelectBanner />
        </StyleRoot>
      )}
    </>
  );
});
